import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import {
  Truck, Plus, Trash, CreditCard, MapPin, CaretDown, Calculator, Path, ShoppingCart, Check,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard, MobileRow } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FreightTime from "@/pages/FreightTime";
import { useCurrency } from "@/context/CurrencyContext";
import { ShipmentPhotos } from "@/components/ShipmentPhotos";
import { COUNTRIES } from "@/lib/countries";

const SYMBOLS = { usd: "$", eur: "€", gbp: "£", inr: "₹", aed: "AED ", cad: "C$", aud: "A$", jpy: "¥" };
const money = (n, cur) =>
  `${SYMBOLS[(cur || "usd").toLowerCase()] || "$"}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const STAGES = ["requested", "quoted", "accepted", "arriving", "booked", "picked_up", "in_transit", "out_for_delivery", "delivered"];
const NEXT = {
  requested: "accepted", quoted: "booked", accepted: "arriving", arriving: "picked_up",
  booked: "picked_up", picked_up: "in_transit",
  in_transit: "out_for_delivery", out_for_delivery: "delivered",
};

const BLANK_BOOKING = {
  description: "", packages: 1, weight_kg: 0, distance_km: 0, rate_id: "",
  pickup_contact: "", pickup_phone: "", pickup_address: "", pickup_city: "", pickup_country: "US", pickup_date: "",
  dropoff_contact: "", dropoff_phone: "", dropoff_address: "", dropoff_city: "", dropoff_country: "US", dropoff_date: "",
  notes: "", from_cart: false, customer_id: "", booking_type: "freight", ride_now: true, direct: false,
  scheduled_at: "", insure: false,
};

const BLANK_RATE = {
  name: "", scope: "local", origin_country: "", dest_country: "", currency: "usd",
  carrier_name: "", base_charge: 0, per_kg: 0, per_km: 0, min_charge: 0, fuel_pct: 0,
  customs_fee: 0, transit_days: 2, active: true, notes: "",
};

const BLANK_CART_ITEM = { source: "inventory", ref_id: "", name: "", quantity: 1, unit: "pcs", unit_price: 0, weight_kg: 0 };

export default function Shipping() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currencies } = useCurrency();
  const [view, setView] = useState("shipments");
  const [tab, setTab] = useState("all");
  const [shipments, setShipments] = useState([]);
  const [rates, setRates] = useState([]);
  const [cart, setCart] = useState({ items: [], goods_value: 0, total_weight_kg: 0, currency: "usd" });
  const [items, setItems] = useState([]);
  const [listings, setListings] = useState([]);
  const [people, setPeople] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [rateTarget, setRateTarget] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [parcelProviders, setParcelProviders] = useState([]);
  const [tracking, setTracking] = useState({});

  const [stars, setStars] = useState(5);
  const [ratingComment, setRatingComment] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK_BOOKING });
  const [rateForm, setRateForm] = useState({ ...BLANK_RATE });
  const [cartForm, setCartForm] = useState({ ...BLANK_CART_ITEM });
  const [editingRate, setEditingRate] = useState(null);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const canManage = ["admin", "vendor"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const selected = options.find((o) => o.rate_id === form.rate_id) || null;

  const load = useCallback(async () => {
    const [s, r, c, i, l, p] = await Promise.all([
      api.get("/shipments", { params: { status: tab } }),
      api.get("/shipping/rates"),
      api.get("/cart"),
      api.get("/items"),
      api.get("/listings"),
      api.get("/network/directory"),
    ]);
    setShipments(s.data);
    setRates(r.data);
    setCart(c.data);
    setItems(i.data);
    setListings(l.data);
    setPeople(Array.isArray(p.data) ? p.data : p.data?.people || []);
    if (["admin", "vendor"].includes(user?.role)) {
      const jobs = await api.get("/shipments/open").catch(() => ({ data: [] }));
      setOpenJobs(jobs.data);
    }
  }, [tab, user]);

  useEffect(() => {
    load();
    api.get("/parcels/meta").then(({ data }) => setParcelProviders(data.providers)).catch(() => {});
  }, [load]);

  const saveTracking = async (shipment) => {
    const entry = tracking[shipment.id] || {};
    try {
      await api.post(`/parcels/shipments/${shipment.id}/tracking`, {
        provider: entry.provider || "India Post",
        reference: (entry.reference || "").trim(),
        show_publicly: true,
      });
      toast.success("Tracking saved — your customer can follow it too");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const findServices = async () => {
    setSearching(true);
    try {
      const { data } = await api.post("/shipping/quotes", {
        pickup_country: form.pickup_country,
        dropoff_country: form.dropoff_country,
        weight_kg: Number(form.weight_kg),
        distance_km: Number(form.distance_km),
      });
      setOptions(data.options);
      const best = data.options.find((o) => o.cheapest) || data.options[0];
      if (best) set({ rate_id: best.rate_id });
    } catch (err) {
      setOptions([]);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const openBooking = (fromCart) => {
    setForm({
      ...BLANK_BOOKING,
      from_cart: fromCart,
      weight_kg: fromCart ? cart.total_weight_kg || 0 : 0,
      description: fromCart ? `${cart.items.length} item(s) from load basket` : "",
    });
    setOptions([]);
    setBookOpen(true);
  };

  const book = async (e) => {
    e.preventDefault();
    try {
      const { data: created } = await api.post("/shipments", {
        ...form,
        packages: Number(form.packages),
        weight_kg: Number(form.weight_kg),
        distance_km: Number(form.distance_km),
        rate_id: form.rate_id || null,
        customer_id: form.customer_id || null,
        booking_type: form.booking_type,
        vehicle_type: selected?.rate_name || null,
        ride_now: form.ride_now,
        direct: form.direct,
        scheduled_at: form.ride_now ? null : (form.scheduled_at || null),
        pickup_date: form.pickup_date || null,
        dropoff_date: form.dropoff_date || null,
      });
      toast.success("Pick-up booked");
      setBookOpen(false);
      const wantsCover = form.insure;
      const coverValue = Number(form.from_cart ? cart.goods_value : 0) || Number(created?.total) || 1000;
      setForm({ ...BLANK_BOOKING });
      setOptions([]);
      if (wantsCover && created?.id) {
        navigate(
          `/insurance?shipment=${created.id}&value=${coverValue}` +
          `&country=${form.pickup_country}&dest=${form.dropoff_country}`
        );
        return;
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addCartItem = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cart/items", {
        source: cartForm.source,
        ref_id: cartForm.ref_id || null,
        name: cartForm.name || null,
        quantity: Number(cartForm.quantity),
        unit: cartForm.unit,
        unit_price: Number(cartForm.unit_price),
        weight_kg: Number(cartForm.weight_kg),
      });
      setCart(data);
      setCartOpen(false);
      setCartForm({ ...BLANK_CART_ITEM });
      toast.success("Added to load basket");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeCartItem = async (lineId) => {
    const { data } = await api.delete(`/cart/items/${lineId}`);
    setCart(data);
  };

  const clearCart = async () => {
    const { data } = await api.delete("/cart");
    setCart(data);
  };

  const advance = async (s) => {
    try {
      await api.patch(`/shipments/${s.id}/status`, { status: NEXT[s.status] });
      toast.success(`Marked ${NEXT[s.status].replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const claim = async (s) => {
    try {
      await api.post(`/shipments/${s.id}/claim`);
      toast.success("Booking accepted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const whatsapp = async (s, who) => {
    try {
      const { data } = await api.get(`/shipments/${s.id}/whatsapp`);
      const url = (who === "carrier" ? data.carrier_url : data.customer_url)
        || data.pickup_contact_url || data.share_url;
      window.open(url, "_blank");
      if (!data.carrier_url && who === "carrier") {
        toast.info("That carrier has no WhatsApp number saved — pick a contact in WhatsApp instead");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveWhatsapp = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/me", { phone: waPhone, whatsapp_opt_in: true });
      toast.success("WhatsApp number saved");
      setWaOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decline = async (s) => {
    try {
      await api.post(`/shipments/${s.id}/decline`);
      toast.success("Job declined");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitRating = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/shipments/${rateTarget.id}/rating`, { stars: Number(stars), comment: ratingComment });
      toast.success("Thanks for rating your carrier");
      setRateTarget(null);
      setStars(5);
      setRatingComment("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const cancel = async (s) => {
    try {
      await api.patch(`/shipments/${s.id}/status`, { status: "cancelled" });
      toast.success("Shipment cancelled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeShipment = async (id) => {
    try {
      await api.delete(`/shipments/${id}`);
      toast.success("Shipment deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pay = async (s) => {
    try {
      const { data } = await api.post("/payments/shipment/checkout", {
        shipment_id: s.id,
        origin_url: window.location.origin,
        user_id: user?.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveRate = async (e) => {
    e.preventDefault();
    const body = {
      ...rateForm,
      base_charge: Number(rateForm.base_charge),
      per_kg: Number(rateForm.per_kg),
      per_km: Number(rateForm.per_km),
      min_charge: Number(rateForm.min_charge),
      fuel_pct: Number(rateForm.fuel_pct),
      customs_fee: Number(rateForm.customs_fee),
      transit_days: Number(rateForm.transit_days),
    };
    try {
      if (editingRate) await api.patch(`/shipping/rates/${editingRate}`, body);
      else await api.post("/shipping/rates", body);
      toast.success(editingRate ? "Service updated" : "Service added");
      setRateOpen(false);
      setEditingRate(null);
      setRateForm({ ...BLANK_RATE });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeRate = async (id) => {
    try {
      await api.delete(`/shipping/rates/${id}`);
      toast.success("Service deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const active = shipments.filter((s) => ["booked", "picked_up", "in_transit", "out_for_delivery"].includes(s.status));
  const unpaid = shipments.filter((s) => s.payment_status !== "paid" && s.status !== "cancelled");
  const currencyOptions = Object.keys(currencies || { usd: 1 });

  return (
    <div data-testid="shipping-page">
      <PageHeader
        overline="Transport"
        title="Shipping & Transportation"
        description="Build a load basket, let the app find every local and international service that covers your lane, then book the pick-up and drop-off and pay goods plus freight in one go."
        testid="shipping-header"
        action={
          <div className="flex flex-wrap gap-2">
            <Button data-testid="book-shipment-button" onClick={() => openBooking(false)} className="w-full sm:w-auto">
              <Plus size={16} className="mr-1.5" /> Book pick-up
            </Button>
            <Button
              data-testid="whatsapp-settings-button"
              variant="outline"
              onClick={() => { setWaPhone(user?.phone || ""); setWaOpen(true); }}
              className="w-full sm:w-auto"
            >
              <WhatsappLogo size={16} className="mr-1.5" /> My WhatsApp
            </Button>
            {canManage && (
              <Button
                data-testid="add-rate-button"
                variant="outline"
                onClick={() => {
                  setEditingRate(null);
                  setRateForm({ ...BLANK_RATE });
                  setRateOpen(true);
                }}
                className="w-full sm:w-auto"
              >
                <Calculator size={16} className="mr-1.5" /> New service
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Shipments" value={shipments.length} icon={Truck} testid="stat-shipments" />
        <StatCard label="On the road" value={active.length} tone="primary" icon={Path} testid="stat-active-shipments" />
        <StatCard label="Awaiting payment" value={unpaid.length} tone={unpaid.length ? "danger" : "default"} icon={CreditCard} testid="stat-unpaid-shipments" />
        <StatCard label="In load basket" value={cart.items.length} sub={money(cart.goods_value, cart.currency)} icon={ShoppingCart} testid="stat-cart" />
      </div>

      {/* Load basket */}
      <div className="mb-6 border border-border bg-white" data-testid="cart-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <ShoppingCart size={16} className="text-[#002FA7]" />
            <span className="font-display text-sm font-bold tracking-tight">LOAD BASKET</span>
            <span className="text-xs text-slate-500">
              {cart.items.length} item(s) · {money(cart.goods_value, cart.currency)} · {cart.total_weight_kg} kg
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="add-cart-item-button" onClick={() => { setCartForm({ ...BLANK_CART_ITEM }); setCartOpen(true); }}>
              <Plus size={14} className="mr-1" /> Add item
            </Button>
            <Button size="sm" data-testid="book-cart-button" disabled={!cart.items.length} onClick={() => openBooking(true)}>
              <Truck size={14} className="mr-1" /> Ship this load
            </Button>
            {cart.items.length > 0 && (
              <Button size="sm" variant="ghost" data-testid="clear-cart-button" onClick={clearCart}>
                Clear
              </Button>
            )}
          </div>
        </div>
        {cart.items.length === 0 ? (
          <div className="px-4 py-4 text-sm text-slate-500">
            Add stock items or marketplace adverts here — the shipping charge is calculated on the basket weight and
            goods plus freight are paid together.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="cart-items">
            {cart.items.map((i) => (
              <li key={i.line_id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`cart-item-${i.line_id}`}>
                <div className="min-w-0">
                  <div className="truncate font-semibold text-[#0F172A]">{i.name}</div>
                  <div className="text-xs text-slate-500">
                    {i.quantity} {i.unit} · {money(i.unit_price, i.currency)} each · {i.weight_kg || 0} kg/unit · {i.source}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-mono text-sm font-bold">{money(i.quantity * i.unit_price, i.currency)}</span>
                  <button data-testid={`remove-cart-item-${i.line_id}`} onClick={() => removeCartItem(i.line_id)} className="text-[#FF3B30]">
                    <Trash size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canManage && !rates.some((r) => r.created_by === user?.id) && (
        <div className="mb-6 border border-[#C7D2FE] bg-[#EEF2FF] px-4 py-3 text-sm text-[#002FA7]" data-testid="carrier-onboarding-banner">
          <span className="font-semibold">Start taking bookings:</span> publish your own pick-up service (vehicle, rates,
          transit time) and it appears in every customer's quote — then accept jobs from the job board.
          <Button
            size="sm"
            className="ml-3"
            data-testid="onboard-add-service-button"
            onClick={() => { setEditingRate(null); setRateForm({ ...BLANK_RATE }); setRateOpen(true); }}
          >
            Add my service
          </Button>
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList data-testid="shipping-view-tabs">
          <TabsTrigger value="shipments" data-testid="tab-shipments">Shipments</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">Services</TabsTrigger>
          {canManage && (
            <TabsTrigger value="jobs" data-testid="tab-jobs">
              Job board{openJobs.length ? ` (${openJobs.length})` : ""}
            </TabsTrigger>
          )}
          <TabsTrigger value="times" data-testid="tab-times">Times</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "times" && <FreightTime />}

      {view === "shipments" && (
        <>
          <div className="mb-4 flex gap-2 scroll-x overflow-x-auto pb-1">
            {["all", ...STAGES, "cancelled"].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setTab(s)}
                className={`shrink-0 border px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                  tab === s ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-600"
                }`}
              >
                {s.replace(/_/g, " ")}
              </button>
            ))}
          </div>

          {shipments.length === 0 ? (
            <EmptyState
              title="No shipments yet"
              description="Book a pick-up and drop-off to get a freight quote."
              testid="shipments-empty"
            />
          ) : (
            <div className="border border-border bg-white" data-testid="shipments-list">
              {/* Mobile */}
              <div className="md:hidden">
                {shipments.map((s) => (
                  <MobileRow
                    key={s.id}
                    testid={`shipment-card-${s.id}`}
                    title={s.shipment_number}
                    badges={
                      <>
                        <StatusBadge value={s.status} />
                        <StatusBadge value={s.payment_status === "paid" ? "approved" : "pending"} />
                        <StatusBadge value={s.scope} />
                      </>
                    }
                    fields={[
                      { label: "Pick-up", value: `${s.pickup_city || ""} ${s.pickup_country}` },
                      { label: "Drop-off", value: `${s.dropoff_city || ""} ${s.dropoff_country}` },
                      { label: "Carrier", value: s.carrier || "unassigned" },
                      { label: "Charges", value: money(s.total, s.currency) },
                    ]}
                    actions={
                      <div className="flex flex-col gap-1.5">
                        {s.payment_status !== "paid" && s.status !== "cancelled" && (
                          <Button size="sm" data-testid={`pay-shipment-${s.id}`} onClick={() => pay(s)}>
                            Pay
                          </Button>
                        )}
                        {canManage && NEXT[s.status] && (
                          <Button size="sm" variant="outline" data-testid={`advance-shipment-${s.id}`} onClick={() => advance(s)}>
                            {NEXT[s.status].replace(/_/g, " ")}
                          </Button>
                        )}
                      </div>
                    }
                  />
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden overflow-x-auto md:block">
              <table className="hidden w-full min-w-[900px] text-sm md:table">
                <thead className="border-b border-border bg-slate-50">
                  <tr className="overline text-left text-[0.65rem] text-slate-500">
                    <th className="px-4 py-3">Shipment</th>
                    <th className="px-4 py-3">Route</th>
                    <th className="px-4 py-3">Load</th>
                    <th className="px-4 py-3">Carrier</th>
                    <th className="px-4 py-3">Charges</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s) => (
                    <Fragment key={s.id}>
                      <tr className="border-b border-border last:border-0" data-testid={`shipment-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <button
                            data-testid={`expand-shipment-${s.id}`}
                            onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                            className="flex items-center gap-1.5 font-semibold text-[#0F172A]"
                          >
                            <CaretDown size={13} className={expanded === s.id ? "rotate-180" : ""} />
                            {s.shipment_number}
                          </button>
                          <div className="text-xs text-slate-500">{s.description}</div>
                          {s.customer_name && s.customer_id !== s.created_by && (
                            <div className="overline text-[0.6rem] text-slate-400">for {s.customer_name}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-xs">
                            <MapPin size={13} className="text-slate-400" />
                            {s.pickup_city || s.pickup_country} → {s.dropoff_city || s.dropoff_country}
                          </div>
                          <div className="overline text-[0.6rem] text-slate-400">{s.scope} · {s.transit_days}d</div>
                          {s.scheduled_at && (
                            <div className="text-[0.65rem] font-semibold text-[#0B7A5B]" data-testid={`scheduled-${s.id}`}>
                              Scheduled {s.scheduled_at.replace("T", " ").slice(0, 16)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {s.weight_kg} kg · {s.packages} pkg
                          {s.distance_km ? ` · ${s.distance_km} km` : ""}
                          {s.items?.length ? ` · ${s.items.length} item(s)` : ""}
                        </td>
                        <td className="px-4 py-3 text-xs" data-testid={`shipment-carrier-${s.id}`}>
                          {s.carrier || <span className="text-slate-400">unassigned</span>}
                          <div className="overline text-[0.6rem] text-slate-400">{s.rate_name}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold">
                          {money(s.total, s.currency)}
                          {s.goods_value > 0 && (
                            <div className="text-[0.7rem] font-normal text-slate-500">
                              + goods {money(s.goods_value, s.goods_currency || s.currency)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3"><StatusBadge value={s.status} /></td>
                        <td className="px-4 py-3">
                          <StatusBadge value={s.payment_status === "paid" ? "approved" : "pending"} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {s.payment_status !== "paid" && s.status !== "cancelled" && (
                              <Button size="sm" data-testid={`pay-shipment-${s.id}`} onClick={() => pay(s)}>
                                <CreditCard size={14} className="mr-1" /> Pay
                              </Button>
                            )}
                            {canManage && !s.carrier_id && s.status !== "cancelled" && (
                              <Button size="sm" variant="outline" data-testid={`claim-shipment-${s.id}`} onClick={() => claim(s)}>
                                <Check size={14} className="mr-1" /> Accept
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`whatsapp-shipment-${s.id}`}
                              title="Send the booking details on WhatsApp"
                              onClick={() => whatsapp(s, canManage ? "customer" : "carrier")}
                            >
                              <WhatsappLogo size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`insure-shipment-${s.id}`}
                              title="Insure this load"
                              onClick={() => navigate(
                                `/insurance?shipment=${s.id}&value=${s.goods_value || s.total || 1000}` +
                                `&country=${s.pickup_country}&dest=${s.dropoff_country}`
                              )}
                            >
                              Insure
                            </Button>
                            {canManage && NEXT[s.status] && (
                              <Button size="sm" variant="outline" data-testid={`advance-shipment-${s.id}`} onClick={() => advance(s)}>
                                {NEXT[s.status].replace(/_/g, " ")}
                              </Button>
                            )}
                            {canManage && s.status !== "cancelled" && s.status !== "delivered" && (
                              <Button size="sm" variant="ghost" data-testid={`cancel-shipment-${s.id}`} onClick={() => cancel(s)}>
                                Cancel
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="ghost" data-testid={`delete-shipment-${s.id}`} onClick={() => removeShipment(s.id)}>
                                <Trash size={14} />
                              </Button>
                            )}
                            {s.status === "delivered" && !s.rating && (s.created_by === user?.id || s.customer_id === user?.id) && (
                              <Button size="sm" variant="outline" data-testid={`rate-shipment-${s.id}`} onClick={() => setRateTarget(s)}>
                                Rate
                              </Button>
                            )}
                            {s.rating && (
                              <span className="text-xs font-semibold text-[#002FA7]" data-testid={`rating-${s.id}`}>
                                {"★".repeat(s.rating)}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expanded === s.id && (
                        <tr className="border-b border-border bg-slate-50" data-testid={`shipment-detail-${s.id}`}>
                          <td colSpan={8} className="px-4 py-4">
                            <div className="grid gap-6 lg:grid-cols-3">
                              <div>
                                <div className="overline mb-1 text-[0.6rem] text-[#002FA7]">Pick-up</div>
                                <div className="text-sm font-semibold">{s.pickup_contact}</div>
                                <div className="text-xs text-slate-600">{s.pickup_address}</div>
                                <div className="text-xs text-slate-600">
                                  {s.pickup_city} {s.pickup_country} · {s.pickup_phone || "—"}
                                </div>
                                <div className="text-xs text-slate-500">Ready {s.pickup_date || "—"}</div>
                              </div>
                              <div>
                                <div className="overline mb-1 text-[0.6rem] text-[#002FA7]">Drop-off</div>
                                <div className="text-sm font-semibold">{s.dropoff_contact}</div>
                                <div className="text-xs text-slate-600">{s.dropoff_address}</div>
                                <div className="text-xs text-slate-600">
                                  {s.dropoff_city} {s.dropoff_country} · {s.dropoff_phone || "—"}
                                </div>
                                <div className="text-xs text-slate-500">Deliver by {s.dropoff_date || "—"}</div>
                              </div>
                              <div>
                                <div className="overline mb-1 text-[0.6rem] text-[#002FA7]">Charges — {s.rate_name}</div>
                                <dl className="space-y-1 text-xs">
                                  <div className="flex justify-between"><dt>Base</dt><dd>{money(s.base_charge, s.currency)}</dd></div>
                                  <div className="flex justify-between"><dt>Weight</dt><dd>{money(s.weight_charge, s.currency)}</dd></div>
                                  <div className="flex justify-between"><dt>Distance</dt><dd>{money(s.distance_charge, s.currency)}</dd></div>
                                  <div className="flex justify-between"><dt>Fuel surcharge</dt><dd>{money(s.fuel_surcharge, s.currency)}</dd></div>
                                  {s.scope === "international" && (
                                    <div className="flex justify-between"><dt>Customs / duty</dt><dd>{money(s.customs_fee, s.currency)}</dd></div>
                                  )}
                                  <div className="flex justify-between border-t border-border pt-1 font-bold">
                                    <dt>Freight total</dt><dd>{money(s.total, s.currency)}</dd>
                                  </div>
                                  {s.goods_value > 0 && (
                                    <div className="flex justify-between"><dt>Goods in load</dt><dd>{money(s.goods_value, s.goods_currency || s.currency)}</dd></div>
                                  )}
                                </dl>
                                {s.freight_currency_note && (
                                  <div data-testid={`freight-currency-note-${s.id}`} className="mt-2 border border-[#F6DCA0] bg-[#FFF8E6] px-2 py-1.5 text-[0.7rem] text-[#8A6A00]">
                                    {s.freight_currency_note}
                                  </div>
                                )}
                                {s.tracking_number && (
                                  <div className="mt-2 text-xs text-slate-500">Tracking {s.tracking_number}</div>
                                )}
                                {s.tracking_ref ? (
                                  <div data-testid={`tracking-ref-${s.id}`} className="mt-2 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-2 py-1.5 text-[0.7rem] text-[#0F172A]">
                                    {s.tracking_provider} · {s.tracking_ref}
                                    {s.tracking_url && (
                                      <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`tracking-link-${s.id}`} className="ml-2 font-semibold text-[#002FA7] hover:underline">Track it</a>
                                    )}
                                  </div>
                                ) : (
                                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                    <select data-testid={`tracking-provider-${s.id}`} value={(tracking[s.id]?.provider) || "India Post"} onChange={(e) => setTracking((t) => ({ ...t, [s.id]: { ...t[s.id], provider: e.target.value } }))} className="border border-slate-300 px-1.5 py-1 text-[0.7rem]">
                                      {(parcelProviders || []).map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                    <input data-testid={`tracking-input-${s.id}`} placeholder="Tracking / receipt number" value={(tracking[s.id]?.reference) || ""} onChange={(e) => setTracking((t) => ({ ...t, [s.id]: { ...t[s.id], reference: e.target.value } }))} className="border border-slate-300 px-1.5 py-1 text-[0.7rem]" />
                                    <button type="button" data-testid={`tracking-save-${s.id}`} onClick={() => saveTracking(s)} className="border border-[#002FA7] px-2 py-1 text-[0.7rem] font-semibold text-[#002FA7] hover:bg-[#EEF3FF]">Save</button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <ShipmentPhotos shipment={s} onDone={load} />
                            {s.items?.length > 0 && (
                              <div className="mt-4 border-t border-border pt-3">
                                <div className="overline mb-2 text-[0.6rem] text-slate-500">Load contents</div>
                                <ul className="grid gap-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
                                  {s.items.map((i, idx) => (
                                    <li key={idx}>
                                      {i.name} — {i.quantity} {i.unit} · {money(i.unit_price, i.currency)} each
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {s.events?.length > 0 && (
                              <div className="mt-4 border-t border-border pt-3">
                                <div className="overline mb-2 text-[0.6rem] text-slate-500">Timeline</div>
                                <div className="flex flex-wrap gap-3">
                                  {s.events.map((e, i) => (
                                    <div key={i} className="text-xs text-slate-600">
                                      <span className="font-semibold uppercase">{String(e.stage).replace(/_/g, " ")}</span>
                                      {" · "}{(e.at || "").slice(0, 16).replace("T", " ")}
                                      {e.by ? ` · ${e.by}` : ""}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </>
      )}

      {view === "rates" && (
        rates.length === 0 ? (
          <EmptyState
            title="No services yet"
            description="Carriers add their own local or international services so bookings can be priced."
            testid="rates-empty"
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="rates-list">
            {rates.map((r) => (
              <div key={r.id} className="border border-border bg-white p-4" data-testid={`rate-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-base font-semibold">{r.name}</div>
                    <div className="text-xs font-semibold text-[#002FA7]">{r.carrier_name || "In-house"}</div>
                    <div className="overline text-[0.6rem] text-slate-500">
                      {r.scope} · {r.origin_country || "any"} → {r.dest_country || "any"}
                    </div>
                  </div>
                  <StatusBadge value={r.active ? "active" : "closed"} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs">
                  <div><dt className="text-slate-400">Base</dt><dd className="font-semibold">{money(r.base_charge, r.currency)}</dd></div>
                  <div><dt className="text-slate-400">Per kg</dt><dd className="font-semibold">{money(r.per_kg, r.currency)}</dd></div>
                  <div><dt className="text-slate-400">Per km</dt><dd className="font-semibold">{money(r.per_km, r.currency)}</dd></div>
                  <div><dt className="text-slate-400">Minimum</dt><dd className="font-semibold">{money(r.min_charge, r.currency)}</dd></div>
                  <div><dt className="text-slate-400">Fuel</dt><dd className="font-semibold">{r.fuel_pct}%</dd></div>
                  <div><dt className="text-slate-400">Transit</dt><dd className="font-semibold">{r.transit_days} days</dd></div>
                  {r.scope === "international" && (
                    <div><dt className="text-slate-400">Customs</dt><dd className="font-semibold">{money(r.customs_fee, r.currency)}</dd></div>
                  )}
                </dl>
                {canManage && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`edit-rate-${r.id}`}
                      onClick={() => {
                        setEditingRate(r.id);
                        setRateForm({ ...BLANK_RATE, ...r });
                        setRateOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" data-testid={`delete-rate-${r.id}`} onClick={() => removeRate(r.id)}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {view === "jobs" && canManage && (
        openJobs.length === 0 ? (
          <EmptyState
            title="No open jobs right now"
            description="New pick-up requests from customers land here — accept one to start running it."
            testid="jobs-empty"
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="jobs-list">
            {openJobs.map((s) => (
              <div key={s.id} className="border border-border bg-white p-4" data-testid={`job-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="overline text-[0.6rem] text-[#002FA7]">
                      {s.booking_type === "ride" ? "Ride request" : "Freight booking"} · {s.shipment_number}
                    </div>
                    <div className="truncate font-display text-base font-semibold">{s.description}</div>
                  </div>
                  <StatusBadge value={s.status} />
                </div>
                <div className="mt-2 space-y-1 text-xs text-slate-600">
                  <div className="flex items-start gap-1.5">
                    <MapPin size={13} className="mt-0.5 shrink-0 text-[#0B7A5B]" />
                    <span>{s.pickup_address}, {s.pickup_city} {s.pickup_country}</span>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <MapPin size={13} className="mt-0.5 shrink-0 text-[#FF3B30]" />
                    <span>{s.dropoff_address}, {s.dropoff_city} {s.dropoff_country}</span>
                  </div>
                  <div>
                    {s.weight_kg} kg · {s.packages} pkg{s.distance_km ? ` · ${s.distance_km} km` : ""} · {s.customer_name}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="font-mono text-lg font-bold text-[#002FA7]">{money(s.total, s.currency)}</span>
                  <div className="flex gap-2">
                    <Button size="sm" data-testid={`accept-job-${s.id}`} onClick={() => claim(s)}>
                      <Check size={14} className="mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`whatsapp-job-${s.id}`} onClick={() => whatsapp(s, "customer")}>
                      <WhatsappLogo size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`decline-job-${s.id}`} onClick={() => decline(s)}>
                      Skip
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* WhatsApp settings */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>Your WhatsApp number</DialogTitle>
            <DialogDescription>
              Save it with the country code so carriers and customers can message you the booking details straight away.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveWhatsapp} className="space-y-3">
            <Input data-testid="whatsapp-phone-input" required value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="+15551234567" />
            <DialogFooter>
              <Button type="submit" data-testid="save-whatsapp-button" className="w-full sm:w-auto">Save number</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rating dialog */}
      <Dialog open={!!rateTarget} onOpenChange={(v) => !v && setRateTarget(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="rating-dialog">
          <DialogHeader>
            <DialogTitle>Rate your carrier</DialogTitle>
            <DialogDescription>How did {rateTarget?.carrier || "the carrier"} do on {rateTarget?.shipment_number}?</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRating} className="space-y-3">
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`star-${n}`}
                  onClick={() => setStars(n)}
                  className={`h-11 w-11 border text-lg font-bold transition-colors ${
                    n <= stars ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-400"
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
            <Textarea data-testid="rating-comment-input" rows={2} placeholder="Anything to add?" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} />
            <DialogFooter>
              <Button type="submit" data-testid="submit-rating-button" className="w-full sm:w-auto">Submit rating</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add to load basket */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="cart-item-dialog">
          <DialogHeader>
            <DialogTitle>Add to load basket</DialogTitle>
            <DialogDescription>Pull in a stock item, a marketplace advert, or type anything else you are moving.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addCartItem} className="space-y-3">
            <div>
              <Label>Source</Label>
              <Select value={cartForm.source} onValueChange={(v) => setCartForm({ ...BLANK_CART_ITEM, source: v })}>
                <SelectTrigger data-testid="cart-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventory">Inventory stock</SelectItem>
                  <SelectItem value="listing">Marketplace advert</SelectItem>
                  <SelectItem value="manual">Other / type it in</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cartForm.source === "inventory" && (
              <div>
                <Label>Item</Label>
                <Select value={cartForm.ref_id} onValueChange={(v) => setCartForm({ ...cartForm, ref_id: v })}>
                  <SelectTrigger data-testid="cart-item-select"><SelectValue placeholder="Pick an item" /></SelectTrigger>
                  <SelectContent>
                    {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {cartForm.source === "listing" && (
              <div>
                <Label>Advert</Label>
                <Select value={cartForm.ref_id} onValueChange={(v) => setCartForm({ ...cartForm, ref_id: v })}>
                  <SelectTrigger data-testid="cart-listing-select"><SelectValue placeholder="Pick an advert" /></SelectTrigger>
                  <SelectContent>
                    {listings.filter((l) => l.status === "active").map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {cartForm.source === "manual" && (
              <div>
                <Label>Description</Label>
                <Input data-testid="cart-name-input" value={cartForm.name} onChange={(e) => setCartForm({ ...cartForm, name: e.target.value })} required />
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Qty</Label>
                <Input data-testid="cart-quantity-input" type="number" min="0.01" step="any" value={cartForm.quantity} onChange={(e) => setCartForm({ ...cartForm, quantity: e.target.value })} />
              </div>
              <div>
                <Label>Unit</Label>
                <Input data-testid="cart-unit-input" value={cartForm.unit} onChange={(e) => setCartForm({ ...cartForm, unit: e.target.value })} />
              </div>
              <div>
                <Label>kg each</Label>
                <Input data-testid="cart-weight-input" type="number" min="0" step="any" value={cartForm.weight_kg} onChange={(e) => setCartForm({ ...cartForm, weight_kg: e.target.value })} />
              </div>
            </div>
            {cartForm.source === "manual" && (
              <div>
                <Label>Value each</Label>
                <Input data-testid="cart-price-input" type="number" min="0" step="0.01" value={cartForm.unit_price} onChange={(e) => setCartForm({ ...cartForm, unit_price: e.target.value })} />
              </div>
            )}
            <DialogFooter>
              <Button type="submit" data-testid="submit-cart-item-button" className="w-full sm:w-auto">Add to basket</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Booking dialog */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="book-shipment-dialog">
          <DialogHeader>
            <DialogTitle>Book a pick-up &amp; drop-off</DialogTitle>
            <DialogDescription>
              Enter the route and load, then hit “Find services” to compare every local or international carrier that
              covers the lane.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={book} className="space-y-5">
            {form.from_cart && (
              <div className="border border-[#C7D2FE] bg-[#EEF2FF] px-3 py-2 text-xs text-[#002FA7]" data-testid="from-cart-note">
                Shipping your load basket — {cart.items.length} item(s), {money(cart.goods_value, cart.currency)} of goods,
                {" "}{cart.total_weight_kg} kg. Goods and freight are paid together.
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2 flex flex-wrap gap-2">
                {[["ride", "Ride / instant pick-up"], ["freight", "Freight booking"]].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`booking-type-${value}`}
                    onClick={() => set({ booking_type: value })}
                    className={`flex-1 border px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      form.booking_type === value ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-border bg-white text-slate-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="toggle-schedule"
                  onClick={() => set({ ride_now: !form.ride_now, scheduled_at: "" })}
                  className={`border px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    form.ride_now ? "border-border bg-white text-slate-600" : "border-[#0B7A5B] bg-[#0B7A5B] text-white"
                  }`}
                >
                  {form.ride_now ? "Schedule for later" : "Scheduled"}
                </button>
              </div>
              <div className="sm:col-span-2">
                <Label>What is moving</Label>
                <Input
                  data-testid="shipment-description-input"
                  required
                  value={form.description}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="e.g. 2 pallets of steel brackets"
                />
              </div>
              <div>
                <Label>Packages</Label>
                <Input data-testid="shipment-packages-input" type="number" min="1" value={form.packages} onChange={(e) => set({ packages: e.target.value })} />
              </div>
              <div>
                <Label>Total weight (kg)</Label>
                <Input data-testid="shipment-weight-input" type="number" min="0" step="0.1" required value={form.weight_kg} onChange={(e) => set({ weight_kg: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Distance (km, optional)</Label>
                <Input data-testid="shipment-distance-input" type="number" min="0" value={form.distance_km} onChange={(e) => set({ distance_km: e.target.value })} />
              </div>
            </div>

            {canManage && (
              <div className="sm:col-span-2">
                <Label>Book on behalf of (customer)</Label>
                <Select value={form.customer_id || "self"} onValueChange={(v) => set({ customer_id: v === "self" ? "" : v })}>
                  <SelectTrigger data-testid="shipment-customer-select">
                    <SelectValue placeholder="Myself" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {people.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.company ? `${p.name} — ${p.company}` : p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 border border-border p-3">
                <div className="overline text-[0.6rem] text-[#002FA7]">Pick-up</div>
                <Input data-testid="pickup-contact-input" required placeholder="Contact name" value={form.pickup_contact} onChange={(e) => set({ pickup_contact: e.target.value })} />
                <Input data-testid="pickup-phone-input" placeholder="Phone" value={form.pickup_phone} onChange={(e) => set({ pickup_phone: e.target.value })} />
                <Textarea data-testid="pickup-address-input" required placeholder="Street address" rows={2} value={form.pickup_address} onChange={(e) => set({ pickup_address: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="pickup-city-input" placeholder="City" value={form.pickup_city} onChange={(e) => set({ pickup_city: e.target.value })} />
                  <Select value={form.pickup_country} onValueChange={(v) => set({ pickup_country: v })}>
                    <SelectTrigger data-testid="pickup-country-input"><SelectValue placeholder="Country" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRIES.map(([code, name]) => (
                        <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Ready for collection</Label>
                  <Input data-testid="pickup-date-input" type="date" value={form.pickup_date} onChange={(e) => set({ pickup_date: e.target.value })} />
                </div>
                {!form.ride_now && (
                  <div>
                    <Label className="text-xs">Scheduled time</Label>
                    <Input data-testid="scheduled-at-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => set({ scheduled_at: e.target.value })} />
                  </div>
                )}
              </div>

              <div className="space-y-2 border border-border p-3">
                <div className="overline text-[0.6rem] text-[#002FA7]">Drop-off</div>
                <Input data-testid="dropoff-contact-input" required placeholder="Contact name" value={form.dropoff_contact} onChange={(e) => set({ dropoff_contact: e.target.value })} />
                <Input data-testid="dropoff-phone-input" placeholder="Phone" value={form.dropoff_phone} onChange={(e) => set({ dropoff_phone: e.target.value })} />
                <Textarea data-testid="dropoff-address-input" required placeholder="Street address" rows={2} value={form.dropoff_address} onChange={(e) => set({ dropoff_address: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="dropoff-city-input" placeholder="City" value={form.dropoff_city} onChange={(e) => set({ dropoff_city: e.target.value })} />
                  <Select value={form.dropoff_country} onValueChange={(v) => set({ dropoff_country: v })}>
                    <SelectTrigger data-testid="dropoff-country-input"><SelectValue placeholder="Country" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRIES.map(([code, name]) => (
                        <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Deliver by</Label>
                  <Input data-testid="dropoff-date-input" type="date" value={form.dropoff_date} onChange={(e) => set({ dropoff_date: e.target.value })} />
                </div>
              </div>
            </div>

            <div>
              <Label>Notes for the driver</Label>
              <Textarea data-testid="shipment-notes-input" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
            </div>

            <div className="border border-border bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="overline text-[0.6rem] text-slate-500">Available shipping services</div>
                <Button type="button" size="sm" variant="outline" data-testid="find-services-button" onClick={findServices} disabled={searching}>
                  <Calculator size={14} className="mr-1.5" /> {searching ? "Searching…" : "Find services"}
                </Button>
              </div>
              {options.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="service-options">
                  {options.map((o) => (
                    <button
                      type="button"
                      key={o.rate_id}
                      data-testid={`service-option-${o.rate_id}`}
                      onClick={() => set({ rate_id: o.rate_id })}
                      className={`w-full border p-3 text-left transition-colors duration-150 ${
                        form.rate_id === o.rate_id ? "border-[#002FA7] bg-white ring-1 ring-[#002FA7]" : "border-border bg-white hover:border-slate-400"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-[#0F172A]">{o.rate_name}</div>
                          <div className="text-xs text-slate-500">
                            {o.carrier_name || "In-house"} · {o.scope} · {o.transit_days} day transit
                          </div>
                          <div className="mt-1 flex gap-1.5">
                            {o.cheapest && (
                              <span data-testid={`cheapest-${o.rate_id}`} className="border border-[#A7E3CE] bg-[#E6F7F1] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#0B7A5B]">
                                Cheapest
                              </span>
                            )}
                            {o.fastest && (
                              <span data-testid={`fastest-${o.rate_id}`} className="border border-[#C7D2FE] bg-[#EEF2FF] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]">
                                Fastest
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-mono text-sm font-bold text-[#002FA7]">{money(o.total, o.currency)}</div>
                          <div className="text-[0.65rem] text-slate-400">
                            fuel {money(o.fuel_surcharge, o.currency)}
                            {o.scope === "international" ? ` · customs ${money(o.customs_fee, o.currency)}` : ""}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selected && (
                <div className="mt-3 border-t border-border pt-2 text-sm font-bold" data-testid="quote-total">
                  Selected: {selected.rate_name} — {money(selected.total, selected.currency)}
                  {form.from_cart && cart.goods_value > 0 && (
                    <span className="font-normal text-slate-500">
                      {" "}+ goods {money(cart.goods_value, cart.currency)} = {money(Number(selected.total) + Number(cart.goods_value), selected.currency)}
                    </span>
                  )}
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs font-normal text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="direct-booking-checkbox"
                      checked={form.direct}
                      onChange={(e) => set({ direct: e.target.checked })}
                      className="h-4 w-4 accent-[#002FA7]"
                    />
                    Book directly online with {selected.carrier_name || "this carrier"} — they are assigned straight away
                    instead of the job going to the board
                  </label>
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs font-normal text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="insure-load-checkbox"
                      checked={form.insure}
                      onChange={(e) => set({ insure: e.target.checked })}
                      className="h-4 w-4 accent-[#002FA7]"
                    />
                    Insure this load — compare cargo cover for {form.pickup_country} → {form.dropoff_country} after booking
                  </label>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" data-testid="submit-shipment-button" className="w-full sm:w-auto">
                Confirm booking
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service / rate card dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="rate-dialog">
          <DialogHeader>
            <DialogTitle>{editingRate ? "Edit service" : "New shipping service"}</DialogTitle>
            <DialogDescription>
              Charges = base + per kg + per km, floored at the minimum, plus fuel % and customs on international lanes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveRate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Service name</Label>
                <Input data-testid="rate-name-input" required value={rateForm.name} onChange={(e) => setRateForm({ ...rateForm, name: e.target.value })} placeholder="Same-day city van" />
              </div>
              <div className="col-span-2">
                <Label>Carrier / company</Label>
                <Input data-testid="rate-carrier-input" value={rateForm.carrier_name || ""} onChange={(e) => setRateForm({ ...rateForm, carrier_name: e.target.value })} placeholder="Defaults to your company" />
              </div>
              <div>
                <Label>Scope</Label>
                <Select value={rateForm.scope} onValueChange={(v) => setRateForm({ ...rateForm, scope: v })}>
                  <SelectTrigger data-testid="rate-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={rateForm.currency} onValueChange={(v) => setRateForm({ ...rateForm, currency: v })}>
                  <SelectTrigger data-testid="rate-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map((c) => (
                      <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Origin country (blank = any)</Label>
                <Select value={rateForm.origin_country || "any"} onValueChange={(v) => setRateForm({ ...rateForm, origin_country: v === "any" ? "" : v })}>
                  <SelectTrigger data-testid="rate-origin-input"><SelectValue placeholder="Any country" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="any">Any country</SelectItem>
                    {COUNTRIES.map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination country (blank = any)</Label>
                <Select value={rateForm.dest_country || "any"} onValueChange={(v) => setRateForm({ ...rateForm, dest_country: v === "any" ? "" : v })}>
                  <SelectTrigger data-testid="rate-dest-input"><SelectValue placeholder="Any country" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="any">Any country</SelectItem>
                    {COUNTRIES.map(([code, name]) => (
                      <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Base charge</Label>
                <Input data-testid="rate-base-input" type="number" step="0.01" value={rateForm.base_charge} onChange={(e) => setRateForm({ ...rateForm, base_charge: e.target.value })} />
              </div>
              <div>
                <Label>Per kg</Label>
                <Input data-testid="rate-perkg-input" type="number" step="0.01" value={rateForm.per_kg} onChange={(e) => setRateForm({ ...rateForm, per_kg: e.target.value })} />
              </div>
              <div>
                <Label>Per km</Label>
                <Input data-testid="rate-perkm-input" type="number" step="0.01" value={rateForm.per_km} onChange={(e) => setRateForm({ ...rateForm, per_km: e.target.value })} />
              </div>
              <div>
                <Label>Minimum charge</Label>
                <Input data-testid="rate-min-input" type="number" step="0.01" value={rateForm.min_charge} onChange={(e) => setRateForm({ ...rateForm, min_charge: e.target.value })} />
              </div>
              <div>
                <Label>Fuel surcharge %</Label>
                <Input data-testid="rate-fuel-input" type="number" step="0.1" value={rateForm.fuel_pct} onChange={(e) => setRateForm({ ...rateForm, fuel_pct: e.target.value })} />
              </div>
              <div>
                <Label>Customs / duty fee</Label>
                <Input data-testid="rate-customs-input" type="number" step="0.01" value={rateForm.customs_fee} onChange={(e) => setRateForm({ ...rateForm, customs_fee: e.target.value })} />
              </div>
              <div>
                <Label>Transit days</Label>
                <Input data-testid="rate-transit-input" type="number" min="1" value={rateForm.transit_days} onChange={(e) => setRateForm({ ...rateForm, transit_days: e.target.value })} />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <Switch data-testid="rate-active-switch" checked={!!rateForm.active} onCheckedChange={(v) => setRateForm({ ...rateForm, active: v })} />
                <span className="text-sm">Active</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-rate-button" className="w-full sm:w-auto">
                {editingRate ? "Save service" : "Add service"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
